import { EventAttendanceModeType, EventStatusType, LocationType, AvailabilityType } from './jsonLdTypes'

/**
 * Type definitions for ToccoEvent JSON-LD structured data
 */
export interface ToccoEventJsonLdData {
  name: string
  startDate: string
  endDate?: string
  location?: {
    type: LocationType
    name?: string
    url?: string
    address?: {
      streetAddress?: string
      addressLocality?: string
      postalCode?: string
      addressRegion?: string
      addressCountry?: string
    }
  }
  image?: string
  description?: string
  offers?: {
    price?: string
    priceCurrency?: string
    availability: AvailabilityType
    url?: string
  }
  isAccessibleForFree?: boolean
  eventAttendanceMode?: EventAttendanceModeType
}

/**
 * Generates JSON-LD structured data for Tocco event pages
 *
 * @param data Object containing Tocco event details
 * @returns JSON-LD object for schema.org Event type
 */
export const generateToccoEventJsonLd = (data: ToccoEventJsonLdData) => {
  // Base JSON-LD object with required properties
  const jsonLd: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: data.name,
    startDate: data.startDate,
    eventAttendanceMode: data.eventAttendanceMode || EventAttendanceModeType.Offline,
    eventStatus: EventStatusType.Scheduled,
    // organizer: {
    //   '@type': 'Organization',
    //   name: 'Careum',
    //   url: 'https://careum.ch',
    // },
  }

  // Add optional properties only if they exist
  if (data.endDate) {
    jsonLd.endDate = data.endDate
  }

  if (data.description) {
    jsonLd.description = data.description
  }

  if (data.image) {
    jsonLd.image = [data.image]
  }

  // Add location if provided
  if (data.location?.type) {
    jsonLd.location = {
      '@type': data.location.type || LocationType.Place,
    }

    if (data.location.name) {
      jsonLd.location.name = data.location.name
    }

    // Add URL for VirtualLocation
    if (data.location.type === LocationType.VirtualLocation && data.location.url) {
      jsonLd.location.url = data.location.url
    }

    // Add address if available (only for Place type)
    if (data.location.type !== LocationType.VirtualLocation && data.location.address) {
      const address = data.location.address
      const postalAddress: Record<string, string> = {
        '@type': 'PostalAddress',
      }

      if (address.streetAddress) postalAddress.streetAddress = address.streetAddress
      if (address.addressLocality) postalAddress.addressLocality = address.addressLocality
      if (address.postalCode) postalAddress.postalCode = address.postalCode
      if (address.addressRegion) postalAddress.addressRegion = address.addressRegion
      if (address.addressCountry) postalAddress.addressCountry = address.addressCountry

      // Only add address if it has at least one property
      if (Object.keys(postalAddress).length > 1) {
        jsonLd.location.address = postalAddress
      }
    }
  }

  // Add offers if provided.
  if (data.offers) {
    jsonLd.offers = {
      '@type': 'Offer',
      price: data.offers.price,
      priceCurrency: data.offers.priceCurrency || 'CHF',
      availability: data.offers.availability,
      url: data.offers.url,
    }
  }

  return jsonLd
}
